import { supabase } from '../lib/supabase';
import type {
  AudioJob,
  CreditsStatus,
  Episode,
  Production,
  Voice,
  JobLog,
  Avatar,
  VideoJob,
} from '../types/db';

function ensureClient() {
  if (!supabase) throw new Error('Supabase client not initialized — check env vars');
  return supabase;
}

// ── Audio Jobs ────────────────────────────────────────────

export async function getJobs(clientId: string): Promise<AudioJob[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('audio_jobs')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as AudioJob[]) ?? [];
}

export async function getJob(jobId: string): Promise<AudioJob | null> {
  const sb = ensureClient();
  const { data, error } = await sb.from('audio_jobs').select('*').eq('id', jobId).single();
  if (error) throw error;
  return (data as AudioJob) ?? null;
}

/**
 * Sprint K: createJob invoca l'orchestrator con production_id (rinominato da
 * scriptcast_id).
 */
export async function createJob(productionId: string): Promise<AudioJob> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-orchestrator', {
    body: { production_id: productionId },
  });
  if (error) throw new Error(error.message);
  const jobId = (data as { job_id: string }).job_id;
  const job = await getJob(jobId);
  if (!job) throw new Error('Orchestrator returned job_id but job not found');
  return job;
}

/**
 * Carica un'edizione + la production collegata (nome + brand) in un'unica
 * query. RLS filtra per client_id.
 */
export async function getEpisode(audioJobId: string): Promise<Episode | null> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('audio_jobs')
    .select('*, production:productions(id, name, brand_name)')
    .eq('id', audioJobId)
    .single();
  if (error) throw error;
  return (data as Episode) ?? null;
}

/**
 * Rigenera audio + mix di un'edizione sul testo modificato dal redattore.
 * Salta fetch articoli + script Claude; riparte da TTS. Tempo atteso 20-25s.
 */
export async function regenerateEpisode(
  audioJobId: string,
  editedScriptText: string,
): Promise<{
  job_id: string;
  status: string;
  audio_url: string | null;
  duration_sec: number | null;
  regeneration_count: number;
  script_text_edited: boolean;
}> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-regenerate-episode', {
    body: { audio_job_id: audioJobId, edited_script_text: editedScriptText },
  });
  if (error) throw new Error(error.message);
  return data as {
    job_id: string;
    status: string;
    audio_url: string | null;
    duration_sec: number | null;
    regeneration_count: number;
    script_text_edited: boolean;
  };
}

// ── Voices ────────────────────────────────────────────────

export async function listVoices(clientId: string): Promise<Voice[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('voices')
    .select('*')
    .or(`client_id.eq.${clientId},client_id.is.null`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as Voice[]) ?? [];
}

// ── Job Logs ──────────────────────────────────────────────

export async function getJobLogs(jobId: string): Promise<JobLog[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('job_logs')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as JobLog[]) ?? [];
}

// ── Vodcast ───────────────────────────────────────────────

export async function listAvatars(clientId: string): Promise<Avatar[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('avatars')
    .select('*')
    .or(`client_id.eq.${clientId},client_id.is.null`)
    .order('is_default', { ascending: false });
  if (error) throw error;
  return (data as Avatar[]) ?? [];
}

export async function getVideoJobsByAudioJob(audioJobId: string): Promise<VideoJob[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('video_jobs')
    .select('*')
    .eq('audio_job_id', audioJobId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as VideoJob[]) ?? [];
}

export async function createVideoJob(input: {
  audio_job_id: string;
  avatar_id: string;
  background: { type: 'color' | 'image' | 'video'; value: string };
  dimensions: Array<'16:9' | '9:16' | '1:1'>;
}): Promise<VideoJob[]> {
  const sb = ensureClient();
  const { data, error } = await sb.functions.invoke('scriptto-generate-video', {
    body: input,
  });
  if (error) throw new Error(error.message);
  return data as VideoJob[];
}

// ── Productions ───────────────────────────────────────────
// Sprint K: collapsed sources + scriptcasts → productions.

export async function listProductions(): Promise<Production[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('productions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Production[]) ?? [];
}

export async function getProduction(id: string): Promise<Production | null> {
  const sb = ensureClient();
  const { data, error } = await sb.from('productions').select('*').eq('id', id).single();
  if (error) throw error;
  return (data as Production) ?? null;
}

export async function createProduction(
  input: Omit<Production, 'id' | 'created_at'>,
): Promise<Production> {
  const sb = ensureClient();
  const { data, error } = await sb.from('productions').insert(input).select().single();
  if (error) throw error;
  return data as Production;
}

export async function updateProduction(
  id: string,
  input: Partial<Omit<Production, 'id' | 'created_at'>>,
): Promise<Production> {
  const sb = ensureClient();
  const { data, error } = await sb.from('productions').update(input).eq('id', id).select().single();
  if (error) throw error;
  return data as Production;
}

export async function pauseProduction(id: string): Promise<Production> {
  return updateProduction(id, { status: 'paused' });
}

export async function activateProduction(id: string): Promise<Production> {
  return updateProduction(id, { status: 'active' });
}

/**
 * Sprint Pricing v0: legge v_client_credits_status del client loggato.
 * RLS filtra per current_client_id() della vista.
 */
export async function getCreditsStatus(clientId: string): Promise<CreditsStatus | null> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('v_client_credits_status')
    .select('*')
    .eq('client_id', clientId)
    .single();
  if (error) {
    // se la vista non ritorna nulla (es. client appena creato senza period_renewal)
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return (data as CreditsStatus) ?? null;
}

export async function listEpisodesByProduction(
  productionId: string,
  limit = 50,
): Promise<AudioJob[]> {
  const sb = ensureClient();
  const { data, error } = await sb
    .from('audio_jobs')
    .select('*')
    .eq('production_id', productionId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data as AudioJob[]) ?? [];
}
